// src/components/Editprofile.js
import React, { useEffect, useMemo, useState } from "react";
import { MsalAuthenticationTemplate } from "@azure/msal-react";
import { InteractionType } from "@azure/msal-browser";
import { loginRequest, protectedResources } from "../authConfig";
import useAuthToken from "../hooks/useAuthToken";
import { useUser } from "../utils/userContext";
import { createApiClient } from "../utils/apiClient";
import type { StateInfo } from "../types/UserTypes";
import styles from './EditProfile.module.css';
import Toast from "./Toast";
import SearchableSelect from "./SearchableSelect";

const EMPTY_USER_INFO = {
  userId: "",
  roleId: 4,
  businessName: "",
  firstName: "",
  lastName: "",
  streetAddress: "",
  city: "",
  state: 0,
  zipCode: "",
  phoneNumber: "",
  emailAddress: "",
  dayRate: 0,
  taxId: "",
  lastLogin: "",
};

const EditProfile = () => {
  const authRequest = { ...loginRequest };
  const { user, loading, activeTenantId, refreshUser } = useUser();
  const userId = user?.userId;
  const { token, getToken } = useAuthToken();

  const api = useMemo(() => {
    if (!token || !activeTenantId || !user?.userId) return null;

    return createApiClient({
      baseUrl: protectedResources.baseURL.endpoint,
      token,
      getToken,
      tenantId: activeTenantId,
      userId: user?.userId,
    });
  }, [token, getToken, activeTenantId, user?.userId]);

  const [states, setStates] = useState<StateInfo[]>([]);
  const [userInfo, setUserInfo] = useState(EMPTY_USER_INFO);

  const [updateStatus, setUpdateStatus] = useState<"success" | "error" | null>(null);
  const [updateMessage, setUpdateMessage] = useState("");
  const [taxIdVisible, setTaxIdVisible] = useState(false);
  const [revealedTaxId, setRevealedTaxId] = useState<string | null>(null);

  const toggleTaxIdVisibility = async () => {
    if (taxIdVisible) {
      if (!api || !userId) return;
      let freshTaxId = "";
      try {
        const result = await api.get(`userinfo`);
        const u = (Array.isArray(result) ? result[0] : result) || {};
        freshTaxId = u.taxId ?? "";
      } catch { /* keep current state */ }

      if (!freshTaxId) return; // no saved value — stay in edit mode

      setUserInfo((prev: any) => ({ ...prev, taxId: freshTaxId }));
      setTaxIdVisible(false);
      setRevealedTaxId(null);
      return;
    }
    if (!userId || !api) return;
    try {
      const data = await api.get(`userinfo/taxid`);
      const real = typeof data === "string" ? data : (data as any)?.taxId ?? "";
      setRevealedTaxId(real);
      setTaxIdVisible(true);
    } catch {
      setRevealedTaxId(null);
    }
  };

  const showStatus = (status: any, message: any) => {
    setUpdateStatus(status);
    setUpdateMessage(message);
  };

  useEffect(() => {
    if (!api) return;

    (async () => {
      try {
        const data = await api.get("stateinfo/");
        setStates(Array.isArray(data) ? data : []);
      } catch (e: any) {
        console.error(e);
        showStatus("error", e?.message || "Failed to load states.");
      }
    })();
  }, [api]);

  useEffect(() => {
    if (!api || loading || !userId) return;

    (async () => {
      try {
        const result = await api.get(`userinfo`);
        const u = (Array.isArray(result) ? result[0] : result) || {};

        setUserInfo({
          userId: u.userId ?? "",
          roleId: u.roleId ?? 0,
          businessName: u.businessName ?? "",
          firstName: u.firstName ?? "",
          lastName: u.lastName ?? "",
          streetAddress: u.streetAddress ?? "",
          city: u.city ?? "",
          state: Number(u.state ?? 0),
          zipCode: u.zipCode ?? "",
          phoneNumber: u.phoneNumber ?? "",
          emailAddress: u.emailAddress ?? "",
          dayRate: u.dayRate ?? 0,
          taxId: u.taxId ?? "",
          lastLogin: u.lastLogin ?? "",
        });
        if (!u.taxId) setTaxIdVisible(true);
      } catch (e: any) {
        console.error(e);
        showStatus("error", e?.message || "Failed to load profile.");
      }
    })();
  }, [api, loading, userId]);

  const handleInputChange = (e: any) => {
    const { name, value } = e.target;
    setUserInfo((prev) => ({
      ...(prev || EMPTY_USER_INFO),
      [name]: value,
    }));
  };

  const updateUser = async (e: any) => {
    e.preventDefault();
    if (!api || !userId) return;

    try {
      const { taxId, lastLogin, dayRate, roleId, ...rest } = userInfo;
      const payload: any = {
        ...rest,
        state: Number(userInfo.state || 0),
      };
      // Only include taxId if the user revealed and edited it
      if (revealedTaxId !== null) {
        payload.taxId = revealedTaxId;
      }

      await api.put(`userinfo`, payload);
      showStatus("success", "Profile updated successfully.");
      await refreshUser({ silent: true });
    } catch (err: any) {
      console.error(err);
      showStatus("error", err?.message || "Error updating profile.");
    }
  };

  return (
    <MsalAuthenticationTemplate
      interactionType={InteractionType.Redirect}
      authenticationRequest={authRequest}
    >
      <>
        <div className="main-articles editprofile-articles">
          <div className={styles['editprofile-shell']}>
            <form onSubmit={updateUser}>
              <div className={styles['editprofile-card']}>
                <div className={styles['editprofile-card-header']}>
                  <h2 className={styles['editprofile-card-header-title']}>Profile Details</h2>
                </div>
                <div className={styles['editprofile-card-body']}>
                <div className={styles['editprofile-form-grid']}>
                  <div className={`${styles['editprofile-field']} ${styles['editprofile-field--full']}`}>
                    <label className={styles['editprofile-label']} htmlFor="profileBusinessName">
                      Business Name
                    </label>
                    <input
                      id="profileBusinessName"
                      className={styles['editprofile-control']}
                      type="text"
                      name="businessName"
                      value={userInfo.businessName}
                      onChange={handleInputChange}
                    />
                  </div>

                  <div className={`${styles['editprofile-field']} ${styles['editprofile-field--half']}`}>
                    <label className={styles['editprofile-label']} htmlFor="profileFirstName">
                      First Name
                    </label>
                    <input
                      id="profileFirstName"
                      className={styles['editprofile-control']}
                      type="text"
                      name="firstName"
                      value={userInfo.firstName}
                      onChange={handleInputChange}
                    />
                  </div>

                  <div className={`${styles['editprofile-field']} ${styles['editprofile-field--half']}`}>
                    <label className={styles['editprofile-label']} htmlFor="profileLastName">
                      Last Name
                    </label>
                    <input
                      id="profileLastName"
                      className={styles['editprofile-control']}
                      type="text"
                      name="lastName"
                      value={userInfo.lastName}
                      onChange={handleInputChange}
                    />
                  </div>

                  <div className={`${styles['editprofile-field']} ${styles['editprofile-field--full']}`}>
                    <label className={styles['editprofile-label']} htmlFor="profileStreetAddress">
                      Address
                    </label>
                    <input
                      id="profileStreetAddress"
                      className={styles['editprofile-control']}
                      type="text"
                      name="streetAddress"
                      value={userInfo.streetAddress}
                      onChange={handleInputChange}
                    />
                  </div>

                  <div className={`${styles['editprofile-field']} ${styles['editprofile-field--third']}`}>
                    <label className={styles['editprofile-label']} htmlFor="city">
                      City
                    </label>
                    <input
                      id="city"
                      className={styles['editprofile-control']}
                      type="text"
                      name="city"
                      value={userInfo.city}
                      onChange={handleInputChange}
                    />
                  </div>

                  <div className={`${styles['editprofile-field']} ${styles['editprofile-field--third']}`}>
                    <label className={styles['editprofile-label']} htmlFor="state">
                      State
                    </label>
                    <SearchableSelect
                      id="state"
                      className={styles['editprofile-control']}
                      options={states.map((s) => ({ value: s.stateId, label: s.name }))}
                      value={userInfo.state ? Number(userInfo.state) : null}
                      onChange={(val) => setUserInfo((prev: any) => ({ ...prev, state: val }))}
                      placeholder="Search states..."
                    />
                  </div>

                  <div className={`${styles['editprofile-field']} ${styles['editprofile-field--third']}`}>
                    <label className={styles['editprofile-label']} htmlFor="profileZipCode">
                      Zip Code
                    </label>
                    <input
                      id="profileZipCode"
                      className={styles['editprofile-control']}
                      type="text"
                      name="zipCode"
                      value={userInfo.zipCode}
                      onChange={handleInputChange}
                    />
                  </div>

                  <div className={`${styles['editprofile-field']} ${styles['editprofile-field--half']}`}>
                    <label className={styles['editprofile-label']} htmlFor="phoneNumber">
                      Phone Number
                    </label>
                    <input
                      id="phoneNumber"
                      className={styles['editprofile-control']}
                      type="tel"
                      name="phoneNumber"
                      value={userInfo.phoneNumber}
                      onChange={handleInputChange}
                    />
                  </div>

                  <div className={`${styles['editprofile-field']} ${styles['editprofile-field--half']}`}>
                    <label className={styles['editprofile-label']} htmlFor="taxId">
                      Tax ID
                    </label>
                    <div className={styles['editprofile-control-reveal']}>
                      <input
                        id="taxId"
                        className={styles['editprofile-control']}
                        type="text"
                        name="taxId"
                        readOnly={!taxIdVisible}
                        value={taxIdVisible && revealedTaxId !== null ? revealedTaxId : userInfo.taxId}
                        onChange={(e) => {
                          if (taxIdVisible) setRevealedTaxId(e.target.value);
                          handleInputChange(e);
                        }}
                      />
                      <button
                        type="button"
                        className={styles['editprofile-reveal-btn']}
                        onClick={toggleTaxIdVisibility}
                        aria-label={taxIdVisible ? "Hide Tax ID" : "Show Tax ID"}
                      >
                        {taxIdVisible ? (
                          <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/><path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/><line x1="1" y1="1" x2="23" y2="23"/></svg>
                        ) : (
                          <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                        )}
                      </button>
                    </div>
                  </div>

                  <div className={`${styles['editprofile-field']} ${styles['editprofile-field--half']}`}>
                    <label className={styles['editprofile-label']} htmlFor="emailAddress">
                      Email Address
                    </label>
                    <input
                      id="emailAddress"
                      className={styles['editprofile-control']}
                      type="email"
                      name="emailAddress"
                      value={userInfo.emailAddress}
                      onChange={handleInputChange}
                    />
                  </div>

                  <div className={`${styles['editprofile-field']} ${styles['editprofile-field--half']}`}>
                    <label className={styles['editprofile-label']} htmlFor="dayRate">
                      Day Rate
                    </label>
                    <input
                      id="dayRate"
                      className={styles['editprofile-control']}
                      type="number"
                      inputMode="decimal"
                      step="0.01"
                      name="dayRate"
                      value={userInfo.dayRate}
                      readOnly
                      aria-describedby="dayRateHelp"
                    />
                    <div id="dayRateHelp" className={styles['editprofile-helper']}>
                      Day rate is managed by an administrator.
                    </div>
                  </div>

                  <div className={styles['editprofile-form-actions']}>
                    <button
                      type="submit"
                      className={`${styles['editprofile-btn']} ${styles['editprofile-save-btn']}`}
                    >
                      Update
                    </button>
                  </div>
                </div>
                </div>
              </div>
            </form>
          </div>
        </div>
        {updateStatus && (
          <Toast
            message={updateMessage}
            type={updateStatus as "success" | "error"}
            onClose={() => { setUpdateStatus(null); setUpdateMessage(""); }}
          />
        )}
      </>
    </MsalAuthenticationTemplate>
  );
};

export default EditProfile;